/**
 * Firestore loader for the supplier audit export. Reads every active booking
 * travelling in the window plus the subcollections the audit needs, then
 * hands the raw data to shape.js. See docs/supplier-audit-export.md for the
 * read plan and the rules that constrain it (no collectionGroup reads).
 */

import firebase from "firebase"
import { db } from "../../../../utils/firebase"
import { parseMealTimes, shapeBooking, shapeConfirmation, shapeFlights, shapeHotel, shapeInvoice, shapeRound, shapeSupplier, shapeTransport, summariseSuppliers } from "./shape"

/**
 * Bookings processed at once. Matches the booking-suppliers tab and the
 * confirmations report so Firestore never sees too many outstanding requests.
 */
const BATCH_SIZE = 10

/**
 * Run `worker` over `items` with at most `concurrency` in flight, preserving
 * result order. `onBatch(done)` fires as each item completes.
 */
async function mapWithConcurrency(items, concurrency, worker, onBatch) {
	const results = new Array(items.length)
	let cursor = 0
	let done = 0

	async function run() {
		while (cursor < items.length) {
			const index = cursor++
			results[index] = await worker(items[index], index)
			done += 1
			onBatch(done)
		}
	}

	const lanes = Array.from({ length: Math.min(concurrency, items.length) }, run)
	await Promise.all(lanes)
	return results
}

/**
 * Collection snapshot as id -> data, or an empty map when the read fails.
 */
const readCollection = (path) =>
	db
		.collection(path)
		.get()
		.then((snapshot) => {
			const byID = {}
			snapshot.forEach((doc) => (byID[doc.id] = doc.data()))
			return byID
		})
		.catch(() => ({}))

/**
 * Collection snapshot as [{ id, data }], or [] when the read fails.
 */
const readItems = (path) =>
	db
		.collection(path)
		.get()
		.then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, data: doc.data() })))
		.catch(() => [])

/**
 * Latest entry from a confirmations subcollection, or null.
 */
const readLatestConfirmation = (path) =>
	db
		.collection(path)
		.orderBy("created", "desc")
		.limit(1)
		.get()
		.then((snapshot) => snapshot.docs[0]?.data() || null)
		.catch(() => null)

const readDocument = (path) =>
	db
		.doc(path)
		.get()
		.then((doc) => (doc.exists ? doc.data() : null))
		.catch(() => null)

/**
 * Items in the booking's order array first, then anything the array missed in
 * snapshot order, so a stray item is never dropped.
 */
const orderItems = (items, order) => {
	const ordered = Array.isArray(order) ? order.map((id) => items.find((item) => item.id === id)).filter(Boolean) : []
	const remaining = items.filter((item) => !ordered.includes(item))
	return [...ordered, ...remaining]
}

/**
 * Region and country from a CMS record's location. Returns a linked marker
 * even when both are blank so the Suppliers tab can tell "linked but empty"
 * from "no CMS record".
 */
const cmsLocation = (data) => ({
	region: data.location?.region || "",
	country: data.location?.country || "",
})

/**
 * Attach a CMS location to every supplier id the CMS record points at. A record
 * with a filled-in location wins over one without.
 */
const linkLocations = (target, ids, location) => {
	ids.filter(Boolean).forEach((id) => {
		const existing = target[id]
		if (!existing || (!existing.region && !existing.country)) target[id] = location
	})
}

/**
 * Load the lookup collections once and index them for the per-booking pass.
 */
async function loadLookups() {
	const [hotels, courses, transfers, users, cmsHolidays, cmsCourses] = await Promise.all([
		readCollection("hotels"),
		readCollection("courses"),
		readCollection("transfers"),
		readCollection("users"),
		readCollection("CMS_holidays"),
		readCollection("CMS_courses"),
	])

	const userNames = {}
	Object.entries(users).forEach(([id, data]) => {
		userNames[id] = `${data.first_name || ""} ${data.last_name || ""}`.trim()
	})

	const holidayTitles = {}
	const hotelLocations = {}
	Object.entries(cmsHolidays).forEach(([id, data]) => {
		holidayTitles[id] = data.title || ""
		const alternates = Array.isArray(data.bs2_id_hotel_alternates) ? data.bs2_id_hotel_alternates : []
		linkLocations(hotelLocations, [data.bs2_id_hotel, ...alternates], cmsLocation(data))
	})

	const courseLocations = {}
	Object.values(cmsCourses).forEach((data) => {
		const alternates = Array.isArray(data.bs2_id_course_alternates) ? data.bs2_id_course_alternates : []
		linkLocations(courseLocations, [data.bs2_id_course, ...alternates], cmsLocation(data))
	})

	return { hotels, courses, transfers, userNames, holidayTitles, hotelLocations, courseLocations }
}

/**
 * Query the active bookings travelling in the window, oldest first.
 */
async function queryBookings(startMillis, endMillis, limit) {
	let query = db.collection("bookings").where("check_in", ">=", firebase.firestore.Timestamp.fromMillis(startMillis))
	if (typeof endMillis === "number") {
		query = query.where("check_in", "<=", firebase.firestore.Timestamp.fromMillis(endMillis))
	}
	query = query.where("removed", "==", false).orderBy("check_in")
	if (typeof limit === "number" && limit > 0) query = query.limit(limit)
	const snapshot = await query.get()
	return snapshot.docs.map((doc) => ({ id: doc.id, data: doc.data() }))
}

/**
 * Build the whole audit for bookings travelling between startMillis and
 * endMillis (open ended when endMillis is not a number). `limit` caps the
 * number of bookings, earliest travel first; null reads them all.
 *
 * @param {{ startMillis: number, endMillis?: number|null, limit?: number|null, onProgress?: ({ done: number, total: number }) => void }} options
 * @returns {Promise<import("./types").AuditResult>}
 */
export async function loadSupplierAudit({ startMillis, endMillis = null, limit = null, onProgress = () => {} }) {
	const [lookups, bookingDocs] = await Promise.all([loadLookups(), queryBookings(startMillis, endMillis, limit)])
	const { hotels, courses, transfers, userNames, holidayTitles, hotelLocations, courseLocations } = lookups

	// One AuditSupplier object per (kind, id) so the summary and every item
	// share the same reference.
	const supplierCache = new Map()
	const supplierFor = (kind, id, lookup, locations) => {
		const key = `${kind}:${id}`
		if (!supplierCache.has(key)) {
			supplierCache.set(key, shapeSupplier({ id, kind, doc: lookup[id] || null, cms: locations[id] || null }))
		}
		return supplierCache.get(key)
	}

	onProgress({ done: 0, total: bookingDocs.length })

	const bookings = await mapWithConcurrency(
		bookingDocs,
		BATCH_SIZE,
		async ({ id, data }) => {
			const hasTransfer = Boolean(data.transfer)
			const hasFlight = Boolean(data.flight?.itineraryID)

			const [client, hotelItems, roundItems, invoiceItems, transferEntry, flightEntry] = await Promise.all([
				data.client ? readDocument(`clients/${data.client}`) : Promise.resolve(null),
				readItems(`bookings/${id}/hotels`),
				readItems(`bookings/${id}/rounds`),
				readItems(`bookings/${id}/supplier_invoices`),
				hasTransfer ? readLatestConfirmation(`bookings/${id}/transfer_confirmations`) : Promise.resolve(null),
				hasFlight ? readLatestConfirmation(`bookings/${id}/flight_confirmations`) : Promise.resolve(null),
			])

			const orderedHotels = orderItems(hotelItems, data.hotels_order)
			const orderedRounds = orderItems(roundItems, data.rounds_order)

			const [hotelEntries, roundEntries] = await Promise.all([
				Promise.all(orderedHotels.map((hotel) => readLatestConfirmation(`bookings/${id}/hotels/${hotel.id}/confirmations`))),
				Promise.all(orderedRounds.map((round) => readLatestConfirmation(`bookings/${id}/rounds/${round.id}/confirmations`))),
			])

			const invoicesFor = (itemID) =>
				invoiceItems.filter((invoice) => Array.isArray(invoice.data.itemIDs) && invoice.data.itemIDs.includes(itemID)).map((invoice) => shapeInvoice(invoice.id, invoice.data))

			const mealTimes = parseMealTimes(data.direct_booking_details?.preferred_dinner_times)

			const shapedHotels = orderedHotels.map((hotel, index) =>
				shapeHotel({
					id: hotel.id,
					data: hotel.data,
					supplier: supplierFor("hotel", hotel.data.resortID || "", hotels, hotelLocations),
					invoices: invoicesFor(hotel.id),
					confirmation: shapeConfirmation(hotelEntries[index]),
					mealTimes,
				})
			)

			const shapedRounds = orderedRounds.map((round, index) =>
				shapeRound({
					id: round.id,
					data: round.data,
					supplier: supplierFor("course", round.data.courseID || "", courses, courseLocations),
					invoices: invoicesFor(round.id),
					confirmation: shapeConfirmation(roundEntries[index]),
					golfers: data.golfers,
				})
			)

			const transport = shapeTransport({
				booking: data,
				supplier: hasTransfer ? supplierFor("transfer", data.transfer, transfers, {}) : null,
				invoices: hasTransfer ? invoicesFor(data.transfer) : [],
				confirmation: shapeConfirmation(transferEntry),
			})

			const flights = shapeFlights({ booking: data, confirmation: shapeConfirmation(flightEntry) })

			return shapeBooking({
				id,
				data,
				client,
				agentName: userNames[data.agent] || "",
				packageTitle: holidayTitles[data.direct_booking_details?.resort] || "",
				hotels: shapedHotels,
				rounds: shapedRounds,
				transport,
				flights,
			})
		},
		(done) => onProgress({ done, total: bookingDocs.length })
	)

	return {
		bookings,
		suppliers: summariseSuppliers(bookings),
		scanned: bookingDocs.length,
		window: { startMillis, endMillis: typeof endMillis === "number" ? endMillis : null },
		generatedMillis: Date.now(),
	}
}
